import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { FaArrowLeft, FaArrowRight, FaPlus, FaTrash } from 'react-icons/fa';
import { useAuth } from '../hooks/useAuth';
import ImageUpload from './ImageUpload';

const Container = styled.div`
  min-height: 100vh;
  background: ${theme.colors.lightGray};
`;

const Header = styled.div`
  background: ${theme.colors.white};
  padding: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.sm};
  position: sticky;
  top: 0;
  z-index: 10;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: ${theme.colors.lightGray};
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: ${theme.spacing.md};
`;

const ProgressFill = styled.div<{ progress: number }>`
  width: ${props => props.progress}%;
  height: 100%;
  background: ${theme.colors.primary};
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  text-align: center;
  font-weight: 600;
  color: ${theme.colors.secondary};
`;

const Content = styled.div`
  padding: ${theme.spacing.xl};
  max-width: 600px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.md};
  margin-bottom: ${theme.spacing.lg};
`;

const Title = styled.h2`
  font-size: 1.8rem;
  font-weight: bold;
  color: ${theme.colors.secondary};
  margin-bottom: ${theme.spacing.lg};
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const Label = styled.label`
  font-weight: 600;
  color: ${theme.colors.secondary};
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: ${theme.spacing.md};
  border: 2px solid ${theme.colors.lightGray};
  border-radius: ${theme.borderRadius.md};
  font-size: 1rem;
  transition: border-color 0.3s ease;
  
  &:focus {
    border-color: ${theme.colors.primary};
    outline: none;
  }
`;

const Row = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  background: ${theme.colors.primary};
  color: ${theme.colors.white};
  border: none;
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.md};
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  
  &:hover {
    background: ${theme.colors.secondary};
    transform: translateY(-2px);
  }
  
  &:disabled {
    background: ${theme.colors.mediumGray};
    cursor: not-allowed;
    transform: none;
  }
`;

const SecondaryButton = styled(Button)`
  background: ${theme.colors.white};
  color: ${theme.colors.secondary};
  border: 2px solid ${theme.colors.lightGray};
  
  &:hover {
    background: ${theme.colors.lightGray};
    transform: translateY(-2px);
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: space-between;
  margin-top: ${theme.spacing.xl};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const CarCard = styled.div`
  border: 2px solid ${theme.colors.lightGray};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.md};
  position: relative;
`;

const CarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.md};
`;

const CarTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${theme.colors.secondary};
`;

const DeleteButton = styled.button`
  background: ${theme.colors.error};
  color: ${theme.colors.white};
  border: none;
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #dc2626;
  }
`;

// Removed PhotoUpload, PhotoIcon, PhotoText - now using ImageUpload component

const AddCarButton = styled.button`
  background: ${theme.colors.white};
  color: ${theme.colors.primary};
  border: 2px dashed ${theme.colors.primary};
  padding: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  width: 100%;
  
  &:hover {
    background: ${theme.colors.primary};
    color: ${theme.colors.white};
  }
`;

const EnrollmentFlow: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    cars: [] as Array<{
      id: string;
      make: string;
      model: string;
      year: number;
      spec: string;
      photos: string[];
    }>
  });

  const { updateUser } = useAuth();

  const steps = [
    { title: 'Personal Information', progress: 25 },
    { title: 'Phone Number', progress: 50 },
    { title: 'Add Your Cars', progress: 75 },
    { title: 'Complete Setup', progress: 100 }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Complete enrollment
      updateUser({
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        cars: formData.cars.map(car => ({
          ...car,
          createdAt: new Date(),
          updatedAt: new Date()
        })),
        isEnrolled: true
      });
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const addCar = () => {
    const newCar = {
      id: Date.now().toString(),
      make: '',
      model: '',
      year: new Date().getFullYear(),
      spec: '',
      photos: []
    };
    setFormData(prev => ({
      ...prev,
      cars: [...prev.cars, newCar]
    }));
  };

  const updateCar = (carId: string, field: string, value: string | number | string[]) => {
    setFormData(prev => ({
      ...prev,
      cars: prev.cars.map(car =>
        car.id === carId ? { ...car, [field]: value } : car
      )
    }));
  };

  const removeCar = (carId: string) => {
    setFormData(prev => ({
      ...prev,
      cars: prev.cars.filter(car => car.id !== carId)
    }));
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Form>
            <Row>
              <InputGroup>
                <Label>First Name</Label>
                <Input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                />
              </InputGroup>
              <InputGroup>
                <Label>Last Name</Label>
                <Input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                />
              </InputGroup>
            </Row>
          </Form>
        );

      case 1:
        return (
          <Form>
            <InputGroup>
              <Label>Phone Number</Label>
              <Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+971 50 123 4567"
                required
              />
            </InputGroup>
          </Form>
        );

      case 2:
        return (
          <div>
            {formData.cars.map((car) => (
              <CarCard key={car.id}>
                <CarHeader>
                  <CarTitle>Car {formData.cars.indexOf(car) + 1}</CarTitle>
                  <DeleteButton onClick={() => removeCar(car.id)}>
                    <FaTrash />
                  </DeleteButton>
                </CarHeader>
                <Form>
                  <Row>
                    <InputGroup>
                      <Label>Make</Label>
                      <Input
                        type="text"
                        value={car.make}
                        onChange={(e) => updateCar(car.id, 'make', e.target.value)}
                        placeholder="e.g., Toyota"
                      />
                    </InputGroup>
                    <InputGroup>
                      <Label>Model</Label>
                      <Input
                        type="text"
                        value={car.model}
                        onChange={(e) => updateCar(car.id, 'model', e.target.value)}
                        placeholder="e.g., Camry"
                      />
                    </InputGroup>
                  </Row>
                  <Row>
                    <InputGroup>
                      <Label>Year</Label>
                      <Input
                        type="number"
                        value={car.year}
                        onChange={(e) => updateCar(car.id, 'year', parseInt(e.target.value))}
                        min="1900"
                        max={new Date().getFullYear() + 1}
                      />
                    </InputGroup>
                    <InputGroup>
                      <Label>Specification</Label>
                      <Input
                        type="text"
                        value={car.spec}
                        onChange={(e) => updateCar(car.id, 'spec', e.target.value)}
                        placeholder="e.g., V6, Hybrid"
                      />
                    </InputGroup>
                  </Row>
                  <InputGroup>
                    <Label>Car Photos</Label>
                    <ImageUpload
                      images={car.photos}
                      onImagesChange={(photos) => updateCar(car.id, 'photos', photos)}
                      maxImages={5}
                      userId={formData.firstName + formData.lastName} // Use name as temp ID
                      carId={car.id}
                    />
                  </InputGroup>
                </Form>
              </CarCard>
            ))}
            <AddCarButton onClick={addCar}>
              <FaPlus />
              Add Another Car
            </AddCarButton>
          </div>
        );

      case 3:
        return (
          <div style={{ textAlign: 'center' }}>
            <h3 style={{ marginBottom: '1rem', color: theme.colors.secondary }}>
              Welcome to 971 Run!
            </h3>
            <p style={{ color: theme.colors.mediumGray, marginBottom: '2rem' }}>
              Your account has been set up successfully. You can now enjoy all our services.
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return formData.firstName && formData.lastName;
      case 1:
        return formData.phone;
      case 2:
        return true; // Cars are optional
      case 3:
        return true;
      default:
        return false;
    }
  };

  return (
    <Container>
      <Header>
        <ProgressBar>
          <ProgressFill progress={steps[currentStep].progress} />
        </ProgressBar>
        <ProgressText>
          Step {currentStep + 1} of {steps.length}: {steps[currentStep].title}
        </ProgressText>
      </Header>

      <Content>
        <Card>
          <Title>{steps[currentStep].title}</Title>
          {renderStepContent()}
        </Card>

        <ButtonContainer>
          <SecondaryButton onClick={handleBack} disabled={currentStep === 0}>
            <FaArrowLeft />
            Back
          </SecondaryButton>
          <Button onClick={handleNext} disabled={!canProceed()}>
            {currentStep === steps.length - 1 ? 'Complete' : 'Next'}
            <FaArrowRight />
          </Button>
        </ButtonContainer>
      </Content>
    </Container>
  );
};

export default EnrollmentFlow;


